<script setup lang="ts">
import { useCart } from "../composables/useCart";

const props = defineProps<{ activeCategory: string }>();
const emit = defineEmits<{
  (e: "select", id: string): void;
  (e: "open-install"): void;
}>();

const cart = useCart();

const links = [
  { id: "all", label: "All" },
  { id: "shirts", label: "Shirts" },
  { id: "outerwear", label: "Outerwear" },
  { id: "knits", label: "Knits" },
  { id: "trousers", label: "Trousers" },
  { id: "accessories", label: "Accessories" }
];
</script>

<template>
  <header class="header">
    <div class="header__ticker">
      <span>Free shipping over $180</span>
      <span class="header__dot" />
      <span>Made in Porto, Biella, Naples, Como</span>
      <span class="header__dot" />
      <span>OS1 desktop and APK available — quiet, local-first</span>
    </div>
    <div class="header__row os1-shell">
      <button class="header__brand" @click="emit('select', 'all')">
        <span class="header__brand-dot" />
        <span class="header__brand-name">OS1</span>
      </button>
      <nav class="header__nav" aria-label="Collections">
        <button
          v-for="link in links"
          :key="link.id"
          :class="['header__link', { 'is-active': props.activeCategory === link.id }]"
          @click="emit('select', link.id)"
        >
          {{ link.label }}
        </button>
      </nav>
      <div class="header__actions">
        <button class="header__icon" aria-label="Search" title="Search">
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.2" />
            <path d="M13.5 13.5L17 17" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
          </svg>
        </button>
        <button class="header__icon" aria-label="Install OS1" title="Install OS1" @click="emit('open-install')">
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
            <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M3.5 14.5V16a1 1 0 001 1h11a1 1 0 001-1v-1.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
          </svg>
        </button>
        <button class="header__cart" aria-label="Open cart" @click="cart.toggle">
          <span>Bag</span>
          <span class="header__cart-count">{{ cart.count.value }}</span>
        </button>
      </div>
    </div>
    <div class="os1-divider" />
  </header>
</template>

<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(15, 4, 4, 0.84);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--os1-line);
}

.header__ticker {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  font-size: 0.65rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--os1-ink-mute);
  border-bottom: 1px solid var(--os1-line);
  overflow: hidden;
  white-space: nowrap;
}

.header__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--os1-red-deep);
  opacity: 0.6;
}

.header__row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding-block: 0.9rem;
}

.header__brand {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--os1-font-serif);
  font-size: 1.45rem;
  letter-spacing: 0.18em;
  color: var(--os1-paper);
}

.header__brand-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--os1-red);
  box-shadow: 0 0 12px rgba(255, 70, 50, 0.7);
}

.header__brand-name {
  font-weight: 400;
}

.header__nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-inline: auto;
  flex-wrap: wrap;
}

.header__link {
  padding: 0.5rem 0.9rem;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--os1-ink-soft);
  border-radius: 999px;
  transition: color 200ms ease, background 200ms ease;
}

.header__link:hover {
  color: var(--os1-rose);
}

.header__link.is-active {
  color: var(--os1-paper);
  background: rgba(255, 70, 50, 0.14);
}

.header__actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.header__icon {
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  color: var(--os1-ink-soft);
  transition: background 180ms ease, color 180ms ease;
}

.header__icon:hover {
  background: rgba(241, 217, 200, 0.08);
  color: var(--os1-paper);
}

.header__cart {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-left: 0.3rem;
  padding: 0.55rem 0.9rem;
  border: 1px solid var(--os1-line-strong);
  border-radius: 999px;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--os1-ink);
  transition: border-color 180ms ease, color 180ms ease;
}

.header__cart:hover {
  border-color: var(--os1-rose);
  color: var(--os1-rose);
}

.header__cart-count {
  display: inline-grid;
  place-items: center;
  min-width: 22px;
  height: 22px;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: var(--os1-red);
  color: #1a0303;
  font-weight: 600;
  font-size: 0.65rem;
  letter-spacing: 0;
}

@media (max-width: 820px) {
  .header__nav {
    order: 3;
    width: 100%;
    overflow-x: auto;
    margin-inline: 0;
    flex-wrap: nowrap;
    padding-bottom: 0.2rem;
  }
  .header__row {
    flex-wrap: wrap;
  }
}
</style>
